import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { roleLabel } from "@/lib/format";
import { cn } from "@/lib/utils";

const ROLES = [
  { v: "partner", l: "Partner" },
  { v: "manager", l: "Manager" },
  { v: "responsible_person", l: "Responsible Person" },
  { v: "administrator", l: "Administrator" },
];

const COMP_TYPES = ["VAT", "Audit", "Corporate Tax", "Excise", "Liquidation Report", "SRTIP", "Accounting"];
const PRO_TYPES = [
  "Trade License", "Tenancy Contract", "Establishment Card", "Employment Visa",
  "Partner Visa", "Golden Visa", "Labour Card", "Emirates ID",
  "Medical Insurance", "ILOE", "Liquidation (45 Days)", "WPS (Monthly)",
];

const empty = {
  email: "", name: "", role: "responsible_person", password: "",
  allowed_compliance_types: [], pro_access: false, allowed_pro_types: [],
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, name: u.name, role: u.role, password: "",
      allowed_compliance_types: u.allowed_compliance_types || [],
      pro_access: !!u.pro_access,
      allowed_pro_types: u.allowed_pro_types || [],
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success(`Added ${form.name}`);
      }
      setShowForm(false); setForm(empty); setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const toggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <AppLayout
      title="Users"
      actions={<Button onClick={openNew} data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1.5" /> Add User</Button>}
    >
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="px-4">Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Allowed Types</TableHead>
              <TableHead>PRO Access</TableHead>
              <TableHead className="text-right pr-4">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.id} className="data-row" data-testid={`user-row-${u.id}`}>
                <TableCell className="px-4 font-medium text-slate-900">{u.name}</TableCell>
                <TableCell className="text-slate-700 font-mono-num text-sm">{u.email}</TableCell>
                <TableCell>
                  <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                    {roleLabel(u.role)}
                  </span>
                </TableCell>
                <TableCell className="text-slate-600 text-xs">
                  {(u.allowed_compliance_types || []).length === 0 ? <em className="text-slate-400">All</em> : (u.allowed_compliance_types || []).join(", ")}
                </TableCell>
                <TableCell>
                  {u.pro_access ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <ShieldCheck className="w-3 h-3" /> Yes
                    </span>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-right pr-4">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {u.role !== "administrator" && (
                    <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`del-user-${u.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setForm(empty); setEditing(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>Manage account details, role, and access.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="user-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="user-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Email</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="user-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-600">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-600">Password {editing && <span className="normal-case text-slate-400">(leave blank to keep)</span>}</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} minLength={6} className="mt-1.5 font-mono-num" data-testid="user-password" />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50/50">
              <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Allowed Compliance Types</div>
              <div className="text-[11px] text-slate-500 -mt-1">Leave empty to allow every type.</div>
              <div className="flex flex-wrap gap-2">
                {COMP_TYPES.map((t) => {
                  const on = form.allowed_compliance_types.includes(t);
                  return (
                    <button
                      key={t} type="button"
                      onClick={() => setForm({ ...form, allowed_compliance_types: toggle(form.allowed_compliance_types, t) })}
                      className={cn("text-xs px-2.5 py-1 rounded-full border transition-colors",
                        on ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400")}
                      data-testid={`allow-type-${t.replace(/\s+/g, "-")}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50/50">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <Checkbox checked={form.pro_access} onCheckedChange={(v) => setForm({ ...form, pro_access: !!v })} data-testid="user-pro-access" />
                Grant PRO Compliance access
              </label>
              {form.pro_access && (
                <div>
                  <div className="text-[11px] text-slate-500 mb-1.5">Leave PRO types empty to allow all.</div>
                  <div className="flex flex-wrap gap-2">
                    {PRO_TYPES.map((t) => {
                      const on = form.allowed_pro_types.includes(t);
                      return (
                        <button
                          key={t} type="button"
                          onClick={() => setForm({ ...form, allowed_pro_types: toggle(form.allowed_pro_types, t) })}
                          className={cn("text-[11px] px-2 py-1 rounded-full border transition-colors",
                            on ? "bg-violet-600 text-white border-violet-600" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400")}
                          data-testid={`allow-pro-${t.replace(/\s+/g, "-")}`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="user-save">{editing ? "Save changes" : "Create user"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
